import React, { useMemo } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { Dropdown } from 'primereact/dropdown'

import GroupedButton from '../../../common/components/GroupedButton'
import { ChargerProducts } from '../../../common/enums/Products'
import { Company, Partner } from '../../../common/redux/api/web-api'
import { SLabelText } from '../../../common/styles/Form'
import { CONNECTOR_STATUSES } from '../../../lib/connector-statuses'
import { isAdminDomain, isSupportDomain } from '../../../lib/currentDomain'
import useText from '../../../common/hooks/useText'
import Text from '../../../ui/Text'
import { Input } from '../../../common/uikit/Input'

type FilterDropdownItemsProps = {
    partnerData?: Partner[]
    companies?: Company[]
}

const FilterDropdownItems = ({ companies, partnerData }: FilterDropdownItemsProps) => {
    const text = useText()
    const { control, register, watch } = useFormContext()

    const watchOcppOnly = watch('ocppOnly')
    const selectedPartnerId = watch('partnerId')

    const additionalStatuses = useMemo(() => {
        return CONNECTOR_STATUSES.filter((status) => {
            if (watchOcppOnly === true) return status.chargerTypes.includes('OCPP')
            if (watchOcppOnly === false) return status.chargerTypes.includes('CAPI')
            return status.chargerTypes.length === 2
        })
    }, [watchOcppOnly])

    const chargerProductOptions = Object.keys(ChargerProducts)
        .filter((key) => !isNaN(parseInt(key, 10)))
        .map((key: string) => ({
            label: ChargerProducts[parseInt(key, 10)],
            value: parseInt(key, 10),
        }))
        .sort((a, b) => a.label.localeCompare(b.label))

    return (
        <>
            <div>
                <SLabelText data-testid="ocppOnly-label" className="mt-0 mb-1">
                    <Text code="lblChargerType" />
                </SLabelText>
                <Controller
                    name="ocppOnly"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                        <GroupedButton
                            options={[
                                {
                                    label: text('lblAll'),
                                    value: null,
                                },
                                {
                                    label: text('lblCapiField'),
                                    value: false,
                                },
                                {
                                    label: text('lblOcpp'),
                                    value: true,
                                },
                            ]}
                            onChange={onChange}
                            value={value ?? null}
                        />
                    )}
                />
            </div>
            <div>
                <SLabelText data-testid="product-name-label" className="mt-0 mb-1">
                    <Text code="lblProduct" />
                </SLabelText>

                <Controller
                    name="productId"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                        <Dropdown
                            id="product-id-dropdown"
                            data-testid="product-id"
                            className="w-full"
                            options={chargerProductOptions}
                            showClear
                            onChange={(event) => {
                                onChange(event.value || null)
                            }}
                            value={value}
                        />
                    )}
                />
            </div>
            <div>
                <SLabelText data-testid="charger-status-label" className="mt-0 mb-1">
                    <Text code="lblChargerStatus" />
                </SLabelText>

                <Controller
                    name="status"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                        <Dropdown
                            id="charger-status-dropdown"
                            data-testid="charger-status"
                            className="w-full"
                            options={additionalStatuses.map((status) => ({
                                label: text(status.translationCode),
                                value: status.value,
                            }))}
                            showClear
                            onChange={(event) => {
                                onChange(event.value || null)
                            }}
                            value={value}
                        />
                    )}
                />
            </div>
            <div>
                <SLabelText data-testid="chargepoint-name-label" className="mt-0 mb-1">
                    <Text code="lblChargerNameField" />
                </SLabelText>

                <Input
                    id="chargepoint-name-input"
                    data-testid="chargepoint-name-input"
                    {...register('name')}
                    type="search"
                    autoComplete="off"
                />
            </div>

            <div>
                <SLabelText data-testid="sim-imsi-label" className="mt-0 mb-1">
                    <Text code="lblSimImsiNumber" />
                </SLabelText>

                <Input
                    id="sim-imsi-number-input"
                    data-testid="sim-imsi-number-input"
                    {...register('imsiNumber')}
                    type="search"
                    autoComplete="off"
                />
            </div>
            {isSupportDomain && (
                <>
                    <div>
                        <SLabelText data-testid="firmware-version-label" className="mt-0 mb-1">
                            <Text code="lblFirmwareVersion" />
                        </SLabelText>
                        <Input
                            id=""
                            data-testid="firmware-version-input"
                            {...register('installedFirmwareVersion')}
                            type="search"
                            autoComplete="off"
                        />
                    </div>
                    {partnerData && (
                        <div>
                            <SLabelText data-testid="partner-label" className="mt-0 mb-1">
                                <Text code="lblPartnerField" />
                            </SLabelText>
                            <Controller
                                name="partnerId"
                                control={control}
                                render={({ field: { onChange, value } }) => (
                                    <Dropdown
                                        id="partner-dropdown"
                                        data-testid="partner"
                                        className="w-full"
                                        options={partnerData.map((partner) => ({
                                            label: partner?.name,
                                            value: partner?.id,
                                        }))}
                                        showClear
                                        onChange={(event) => {
                                            onChange(event.value || null)
                                        }}
                                        value={value}
                                        filter
                                        virtualScrollerOptions={{ itemSize: 38 }}
                                    />
                                )}
                            />
                        </div>
                    )}
                </>
            )}
            {!isAdminDomain && companies && (
                <div>
                    <SLabelText data-testid="organisation-label" className="mt-0 mb-1">
                        <Text code="lblOrganisation" />
                    </SLabelText>

                    <Controller
                        name="companyId"
                        control={control}
                        render={({ field: { onChange, value } }) => (
                            <Dropdown
                                id="organisation-dropdown"
                                data-testid="organisation"
                                className="w-full"
                                options={companies
                                    .filter((nextCompany) =>
                                        selectedPartnerId ? nextCompany.partnerId === selectedPartnerId : true
                                    )
                                    .map((company) => ({
                                        label: company.name,
                                        value: company.id,
                                    }))}
                                showClear
                                onChange={(event) => {
                                    onChange(event.value || null)
                                }}
                                filter
                                virtualScrollerOptions={{ itemSize: 38 }}
                                value={value}
                            />
                        )}
                    />
                </div>
            )}
            <div>
                <SLabelText data-testid="owner-name-label" className="mt-0 mb-1">
                    <Text code="lblOwnerName" />
                </SLabelText>
                <Input id="ownerName-input" {...register('ownerName')} type="search" autoComplete="off" />
            </div>
            <div>
                <SLabelText data-testid="owner-email-label" className="mt-0 mb-1">
                    <Text code="lblOwnerEmail" />
                </SLabelText>
                <Input id="ownerEmail-input" {...register('ownerEmail')} type="search" autoComplete="off" />
            </div>
            <div>
                <SLabelText data-testid="owner-mobile-label" className="mt-0 mb-1">
                    <Text code="lblOwnerMobile" />
                </SLabelText>
                <Input id="ownerMobile-input" {...register('ownerMobile')} type="search" autoComplete="off" />
            </div>
        </>
    )
}

export default FilterDropdownItems
